import { useEffect, useState } from 'react'

/**
 * Splits items into rows of alternating sizes (default 6, 5, 6, 5…).
 * Pass `sizes` to change the pattern, e.g. buildRows(items, [4, 3]).
 */
export function buildRows(items, sizes = [6, 5]) {
  const rows = []
  let i = 0
  let rowIndex = 0
  while (i < items.length) {
    const size = sizes[rowIndex % sizes.length]
    rows.push(items.slice(i, i + size))
    i += size
    rowIndex++
  }
  return rows
}

const TYPEWRITER_DEFAULTS = {
  typingSpeed: 90,      // ms per character while typing
  erasingSpeed: 45,     // ms per character while erasing
  pauseAfterType: 1400, // ms to hold the full word before erasing
  pauseAfterErase: 400, // ms pause on empty string before the next word
}

/** Types and erases each word in `words` in a loop; returns the current text. */
export function useTypewriter(words, options) {
  const { typingSpeed, erasingSpeed, pauseAfterType, pauseAfterErase } = {
    ...TYPEWRITER_DEFAULTS,
    ...options,
  }
  const [wordIndex, setWordIndex] = useState(0)
  const [text, setText] = useState('')
  const [phase, setPhase] = useState('typing') // 'typing' | 'pausing' | 'erasing' | 'waiting'

  useEffect(() => {
    const currentWord = words[wordIndex]
    let timer

    if (phase === 'typing') {
      if (text.length < currentWord.length) {
        timer = setTimeout(() => setText(currentWord.slice(0, text.length + 1)), typingSpeed)
      } else {
        timer = setTimeout(() => setPhase('pausing'), pauseAfterType)
      }
    } else if (phase === 'pausing') {
      timer = setTimeout(() => setPhase('erasing'), 0)
    } else if (phase === 'erasing') {
      if (text.length > 0) {
        timer = setTimeout(() => setText(currentWord.slice(0, text.length - 1)), erasingSpeed)
      } else {
        timer = setTimeout(() => setPhase('waiting'), pauseAfterErase)
      }
    } else if (phase === 'waiting') {
      timer = setTimeout(() => {
        setWordIndex((prev) => (prev + 1) % words.length)
        setPhase('typing')
      }, 0)
    }

    return () => clearTimeout(timer)
  }, [text, phase, wordIndex, words, typingSpeed, erasingSpeed, pauseAfterType, pauseAfterErase])

  return text
}

/**
 * Sticky "stacked deck" effect for `.ss-card` children of `containerRef`.
 * Only active at >=992px (matches the max-width: 991px rule in main.css that
 * turns off position:sticky on .ss-card); below that, transforms are cleared.
 */
export function useScrollStack(containerRef, count) {
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const cards = Array.from(container.querySelectorAll('.ss-card'))
    const mql = window.matchMedia('(min-width: 992px)')

    const clearTransforms = () => {
      cards.forEach((card) => { card.style.transform = '' })
    }

    const onScroll = () => {
      if (!mql.matches) return
      const top  = container.getBoundingClientRect().top
      const h    = container.offsetHeight
      const winH = window.innerHeight
      cards.forEach((card, i) => {
        const progress = Math.max(0, Math.min(1, (-top + i * (winH * 0.3)) / (h - winH)))
        if (progress >= 1) {
          card.style.transform = 'scale(0.94) translateY(-24px)'
        } else if (progress > 0) {
          card.style.transform = `scale(${1 - progress * 0.06}) translateY(${-(progress * 24)}px)`
        } else {
          card.style.transform = 'scale(1) translateY(0)'
        }
      })
    }

    const onBreakpointChange = () => {
      if (!mql.matches) clearTransforms()
      onScroll()
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onBreakpointChange)
    // Safari <14 only supports the older addListener/removeListener API.
    if (mql.addEventListener) mql.addEventListener('change', onBreakpointChange)
    else mql.addListener(onBreakpointChange)
    onBreakpointChange()

    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onBreakpointChange)
      if (mql.removeEventListener) mql.removeEventListener('change', onBreakpointChange)
      else mql.removeListener(onBreakpointChange)
    }
  }, [containerRef, count])
}